import type {
  CreateStatusBody,
  CreateTransitionBody,
  CreateWorkflowBody,
  ReorderStatusesBody,
  Transition,
  UpdateStatusBody,
  UpdateTransitionBody,
  UpdateWorkflowBody,
  Workflow,
  WorkflowSummary,
} from '@tasks/contracts';

import { apiRequest, apiRequestList, apiRequestResource, apiRequestVoid } from './client';

/** Ответ мутаций workflow: сам workflow плюс id созданной сущности. */
export type WorkflowWithCreated = Workflow & { readonly createdId?: string };

/** Workflow по умолчанию — то, что показывает проект, пока флоу не выбран. */
export function getWorkflow(key: string, signal?: AbortSignal): Promise<Workflow> {
  return apiRequest<Workflow>(`/projects/${encodeURIComponent(key)}/workflow`, { signal });
}

/**
 * Конкретный workflow проекта. Нужен доске: её колонки — статусы ровно того
 * набора, к которому она привязана, а не флоу по умолчанию (docs/03 §1).
 */
export function getWorkflowById(
  key: string,
  workflowId: string,
  signal?: AbortSignal,
): Promise<Workflow> {
  return apiRequest<Workflow>(`/projects/${encodeURIComponent(key)}/workflow`, {
    query: { workflowId },
    signal,
  });
}

/** Список workflow проекта вместе с привязкой типов задач (docs/05 §5). */
export function listWorkflows(key: string, signal?: AbortSignal): Promise<WorkflowSummary[]> {
  return apiRequestList<WorkflowSummary>(`/projects/${encodeURIComponent(key)}/workflows`, {
    signal,
  });
}

export function createWorkflow(
  key: string,
  body: CreateWorkflowBody,
): Promise<WorkflowSummary> {
  return apiRequestResource<WorkflowSummary>(
    `/projects/${encodeURIComponent(key)}/workflows`,
    'workflow',
    { method: 'POST', body },
  );
}

export function renameWorkflow(
  key: string,
  workflowId: string,
  body: UpdateWorkflowBody,
): Promise<WorkflowSummary> {
  return apiRequestResource<WorkflowSummary>(
    `/projects/${encodeURIComponent(key)}/workflows/${encodeURIComponent(workflowId)}`,
    'workflow',
    { method: 'PATCH', body },
  );
}

export function setDefaultWorkflow(key: string, workflowId: string): Promise<WorkflowSummary> {
  return apiRequestResource<WorkflowSummary>(
    `/projects/${encodeURIComponent(key)}/workflows/${encodeURIComponent(workflowId)}/default`,
    'workflow',
    { method: 'POST' },
  );
}

/**
 * Удаление workflow. Возвращает число перенесённых задач: сервер переводит их
 * в начальный статус workflow по умолчанию, и UI показывает это в тосте.
 */
export function deleteWorkflow(
  key: string,
  workflowId: string,
): Promise<{ readonly movedIssues: number }> {
  return apiRequest<{ movedIssues: number }>(
    `/projects/${encodeURIComponent(key)}/workflows/${encodeURIComponent(workflowId)}`,
    { method: 'DELETE' },
  );
}

/** Привязка типа задачи к workflow: задачи типа переезжают в новый флоу. */
export function bindIssueType(
  key: string,
  issueType: string,
  workflowId: string,
): Promise<{ readonly issueType: string; readonly workflowId: string; readonly movedIssues: number }> {
  return apiRequest(
    `/projects/${encodeURIComponent(key)}/workflow/issue-types/${encodeURIComponent(issueType)}`,
    { method: 'PUT', body: { workflowId } },
  );
}

/**
 * Сервер отвечает обновлённым workflow целиком (`{ statuses, transitions,
 * problems, createdId }`) — новый статус берётся из `createdId` (docs/05 §5).
 */
export function createStatus(key: string, body: CreateStatusBody): Promise<WorkflowWithCreated> {
  return apiRequest<WorkflowWithCreated>(`/projects/${encodeURIComponent(key)}/statuses`, {
    method: 'POST',
    body,
  });
}

/** Обновление статуса тоже возвращает workflow целиком, а не один статус. */
export function updateStatus(key: string, statusId: string, body: UpdateStatusBody): Promise<Workflow> {
  return apiRequest<Workflow>(`/projects/${encodeURIComponent(key)}/statuses/${encodeURIComponent(statusId)}`, {
    method: 'PATCH',
    body,
  });
}

export function deleteStatus(key: string, statusId: string): Promise<void> {
  return apiRequestVoid(`/projects/${encodeURIComponent(key)}/statuses/${encodeURIComponent(statusId)}`, {
    method: 'DELETE',
  });
}

export function reorderStatuses(key: string, body: ReorderStatusesBody): Promise<Workflow> {
  return apiRequest<Workflow>(`/projects/${encodeURIComponent(key)}/statuses/order`, { method: 'PUT', body });
}

export function createTransition(key: string, body: CreateTransitionBody): Promise<Transition> {
  return apiRequest<Transition>(`/projects/${encodeURIComponent(key)}/transitions`, { method: 'POST', body });
}

export function updateTransition(
  key: string,
  transitionId: string,
  body: UpdateTransitionBody,
): Promise<Transition> {
  return apiRequest<Transition>(
    `/projects/${encodeURIComponent(key)}/transitions/${encodeURIComponent(transitionId)}`,
    { method: 'PATCH', body },
  );
}

export function deleteTransition(key: string, transitionId: string): Promise<void> {
  return apiRequestVoid(`/projects/${encodeURIComponent(key)}/transitions/${encodeURIComponent(transitionId)}`, {
    method: 'DELETE',
  });
}
